import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { fetchIntakeAPI, fetchIntakeDocumentsAPI } from "../../service/intakes";
import { documentCountLabel } from "../Matter/IntakeStrip";

/**
 * The intake file: a client who has not signed yet, shown the way a matter
 * is — a header and the documents filed so far (whgdocs-intake-files-design.md).
 *
 * No folders yet; one flat list, newest first. A document opens in the
 * shared full-width viewer by id (the same page the Salesforce console
 * links to), with a way back here. If the client has signed and WHG Docs
 * already holds the matter, this address simply goes to the matter: a
 * signed intake is not a place.
 */
const Page = styled.div`
  padding: 32px 24px;
  background: #fff;
  height: 100%;
  overflow-y: auto;
  font-family: 'Manrope', sans-serif;
  color: #1b2028;
`;

const Crumb = styled.button`
  border: 0;
  background: none;
  padding: 0;
  color: #0c53bb;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin: 12px 0 6px 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 28px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${(p) => (p.$closed ? "#eef0f4" : "#fde9c2")};
  color: ${(p) => (p.$closed ? "#5a6678" : "#7a4f00")};
  font-size: 12px;
  font-weight: 600;
`;

const Facts = styled.div`
  color: #48566b;
  font-size: 13px;
  margin-bottom: 22px;
  & > span + span::before { content: " · "; color: #aab2c0; }
`;

const List = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th {
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    padding: 8px 10px;
    border-bottom: 1px solid #dde2e9;
  }
  td {
    padding: 10px;
    border-bottom: 1px solid #eef0f4;
    vertical-align: middle;
  }
  tbody tr { cursor: pointer; }
  tbody tr:hover { background: #f7f9fc; }
`;

const Name = styled.span`
  font-weight: 600;
  color: #0c53bb;
`;

const Empty = styled.div`
  margin-top: 28px;
  padding: 24px;
  border: 1px dashed #dde2e9;
  border-radius: 8px;
  color: #48566b;
  font-size: 14px;
  line-height: 1.5;
  max-width: 640px;
`;

const Problem = styled.div`
  margin-top: 28px;
  color: #b42318;
  font-size: 14px;
`;

export function formatWhen(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const IntakePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [intake, setIntake] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [problem, setProblem] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProblem("");
    Promise.all([fetchIntakeAPI(id), fetchIntakeDocumentsAPI(id)])
      .then(([header, docs]) => {
        if (cancelled) return;
        setIntake(header?.data || null);
        setDocuments(docs?.data?.results || []);
      })
      .catch((error) => {
        if (cancelled) return;
        setProblem(
          error?.status === 404
            ? "This intake is not in WHG Docs."
            : error?.message || "The intake could not be loaded.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!loading && intake?.matter_id) {
    return <Navigate to={`/matter/${intake.matter_id}`} replace />;
  }

  const openDocument = (doc) => {
    const params = new URLSearchParams({ name: doc.name || "" });
    navigate(`/document/${doc.id}?${params.toString()}`, {
      state: { returnTo: `/intake/${id}`, returnLabel: intake?.client_name || "intake" },
    });
  };

  return (
    <Page data-testid="intake-page">
      <Crumb type="button" onClick={() => navigate("/matter")}>← Matters</Crumb>
      {problem ? <Problem role="alert">{problem}</Problem> : null}
      {intake ? (
        <>
          <Header>
            <Title>{intake.client_name || intake.display_name}</Title>
            <Badge $closed={intake.intake_kind === "closed"} data-testid="intake-badge">
              {intake.badge}
            </Badge>
          </Header>
          <Facts>
            {intake.case_type ? <span>{intake.case_type}</span> : null}
            {intake.status ? <span>Stage: {intake.status}</span> : null}
            {intake.opened_at ? <span>Opened {formatWhen(intake.opened_at)}</span> : null}
            <span>{documentCountLabel(documents.length)}</span>
          </Facts>
          {documents.length ? (
            <List data-testid="intake-documents">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>From</th>
                  <th>Filed</th>
                  <th>By</th>
                  <th>Size</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id} onClick={() => openDocument(doc)} data-testid="intake-document-row">
                    <td><Name>{doc.name}</Name></td>
                    <td>{doc.source || ""}</td>
                    <td>{formatWhen(doc.time_created)}</td>
                    <td>{doc.uploaded_by || ""}</td>
                    <td>{formatSize(doc.size)}</td>
                  </tr>
                ))}
              </tbody>
            </List>
          ) : (
            <Empty data-testid="intake-empty">
              No documents on this intake yet. In Outlook, open the email and use
              <strong> Save to WHG Docs</strong>, then pick this intake — it is listed
              beside the matters, marked “{intake.badge}”. Everything filed here moves
              into the matter the moment the client signs.
            </Empty>
          )}
        </>
      ) : null}
    </Page>
  );
};

export default IntakePage;
